import { useState } from 'react'
import { Outlet } from 'react-router-dom'

import { RouteErrorBoundary } from '@/components/error/RouteErrorBoundary'
import { SubscriptionBanner } from '@/components/subscription/SubscriptionBanner'
import { ActiveCallDock } from '@/features/calling/components/ActiveCallDock'
import { useNativeCalling } from '@/features/calling/sim/useNativeCalling'
import { CreateWorkspaceDialog } from '@/features/layout/components/CreateWorkspaceDialog'
import { useNotificationPoller } from '@/features/notification/hooks/useNotificationPoller'
import { usePushRegistration } from '@/features/notification/push/usePushRegistration'
import { useCallingEnabled } from '@/hooks/useCalling'
import { useRealtime } from '@/hooks/useRealtime'
import { useWorkspaceSettings } from '@/hooks/useWorkspaceSettings'
import { useWorkspaceStore } from '@/stores/workspace.store'
import { cn } from '@/utils/cn'
import { setDateFormat } from '@/utils/date'

import { useLayoutState } from '../hooks/useLayoutState'


import { MobileHeader } from './MobileHeader'
import { Sidebar } from './Sidebar'

interface AppShellProps {
  onOpenCreateModal?: () => void
}

export function AppShell({
  onOpenCreateModal,
}: AppShellProps) {
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false)
  const handleOpenCreateModal = onOpenCreateModal ?? (() => setIsCreateModalOpen(true))

  const {
    isCollapsed,
    isMobileOpen,
    toggleCollapse,
    toggleMobileMenu,
    closeMobileMenu,
  } = useLayoutState()

  const currentWorkspaceId =
    useWorkspaceStore(
      (state) => state.currentWorkspaceId
    )

  // Global notification polling
  useNotificationPoller()
  // Live events (call status) and, in the Android app, phone registration and call-log sync
  useRealtime()
  // Push for everyone: the Android app, and browsers that turned notifications on
  usePushRegistration()
  useNativeCalling()
  const { enabled: callingEnabled } = useCallingEnabled()
  // The workspace decides how dates read; pages are keyed by it so they redraw when it changes.
  const dateFormat = useWorkspaceSettings().settings?.dateFormat
  if (dateFormat) setDateFormat(dateFormat)

  return (
    <div className="relative flex h-[100dvh] min-h-0 flex-col overflow-hidden bg-neutral-50 lg:flex-row">
      {/* Mobile Header */}
      <MobileHeader
        isMobileOpen={isMobileOpen}
        onToggleMobileMenu={toggleMobileMenu}
        onOpenCreateModal={handleOpenCreateModal}
      />

      {/* Sidebar */}
      <Sidebar
        isCollapsed={isCollapsed}
        isMobileOpen={isMobileOpen}
        onToggleCollapse={toggleCollapse}
        onCloseMobileMenu={closeMobileMenu}
        onOpenCreateModal={handleOpenCreateModal}
      />

      {/* Main Content */}
      <div
        className={cn(
          'flex min-h-0 min-w-0 flex-1 flex-col transition-all duration-300 ease-in-out',
          isCollapsed
            ? 'lg:pl-16'
            : 'lg:pl-64'
        )}
      >
        <SubscriptionBanner />

        <main className="min-h-0 min-w-0 flex-1 overflow-y-auto p-4 lg:p-6">
          <RouteErrorBoundary>
            <Outlet
              key={`${currentWorkspaceId || 'default'}:${dateFormat ?? 'dd/MM/yyyy'}`}
            />
          </RouteErrorBoundary>
        </main>
      </div>

      {/* Live and just-ended calls */}
      {callingEnabled && <ActiveCallDock />}

      {/* Create Workspace Dialog */}
      <CreateWorkspaceDialog
        open={isCreateModalOpen}
        onOpenChange={setIsCreateModalOpen}
      />
    </div>
  )
}
